'use client';

import React, { useMemo, useState } from "react";
import { Plus, Search, Loader2, Users } from "lucide-react";
import { useIndividualsCtx } from "./internals";
import { useIndividualsData } from "./hooks/useIndividualsData";
import { IndividualRow } from "./components/IndividualRow";
import { IndividualDrawer } from "./components/IndividualDrawer";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "inactive", label: "Inactive" },
];

export const IndividualsPage = () => {
  const { copy } = useIndividualsCtx();
  const { individuals, loading, error, refresh } = useIndividualsData();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return individuals.filter((a) => {
      if (filter !== "all" && (a.status || "active") !== filter) return false;
      if (!q) return true;
      return [a.name, a.phone, a.city, a.email].filter(Boolean).some((f) => f.toLowerCase().includes(q));
    });
  }, [individuals, query, filter]);

  const openNew = () => {
    setEditing(null);
    setDrawerOpen(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setDrawerOpen(true);
  };

  return (
    <main data-testid="individuals-page" className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
      <header className="mb-5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-zinc-900 sm:text-2xl">{copy.page_title}</h1>
          <p className="mt-1 text-sm text-zinc-500">{copy.page_subtitle}</p>
        </div>
        <button type="button" data-testid="individuals-add-btn" onClick={openNew}
          className="inline-flex shrink-0 items-center gap-2 rounded-md bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700">
          <Plus size={15} />
          <span className="hidden sm:inline">{copy.cta_add}</span>
        </button>
      </header>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-sm flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -tranzinc-y-1/2 text-zinc-400" />
          <input
            data-testid="individuals-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, phone or city"
            className="w-full rounded-md border border-zinc-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-zinc-900"
          />
        </div>
        <div className="flex gap-1">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" data-testid={`individuals-filter-${f.key}`} onClick={() => setFilter(f.key)}
              className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                filter === f.key ? "bg-rose-600 text-white" : "text-zinc-600 hover:bg-zinc-100"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div data-testid="individuals-loading" className="flex items-center justify-center rounded-xl border border-dashed border-zinc-300 bg-white px-6 py-16 text-sm text-zinc-500">
          <Loader2 size={16} className="mr-2 animate-spin" /> Loading…
        </div>
      ) : error ? (
        <div data-testid="individuals-error" className="rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-sm text-red-700">
          {error}
          <button type="button" onClick={refresh} className="ml-3 underline underline-offset-2">Try again</button>
        </div>
      ) : visible.length === 0 ? (
        <div data-testid="individuals-empty-state" className="rounded-xl border border-dashed border-zinc-300 bg-white px-6 py-12 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100">
            <Users size={22} className="text-zinc-500" />
          </div>
          <h3 className="mt-4 text-base font-semibold text-zinc-900">
            {individuals.length === 0 ? "No customers yet" : "No matches"}
          </h3>
          <p className="mt-1 text-sm text-zinc-500">
            {individuals.length === 0 ? "Individual customers you win are added here automatically — or add one yourself." : "Try a different search or filter."}
          </p>
          {individuals.length === 0 && (
            <button type="button" data-testid="individuals-empty-add-btn" onClick={openNew}
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700">
              <Plus size={15} /> {copy.cta_add}
            </button>
          )}
        </div>
      ) : (
        <ul data-testid="individuals-list" className="space-y-2">
          {visible.map((a) => (
            <li key={a.id}>
              <IndividualRow individual={a} onClick={() => openEdit(a)} />
            </li>
          ))}
        </ul>
      )}

      <IndividualDrawer open={drawerOpen} individual={editing} onClose={() => setDrawerOpen(false)} onChanged={refresh} />
    </main>
  );
};
